// ---------------------
// Custom Dropdown
// Example Usage:

// <custom-dropdown position="right">
//   <span slot="label">Select an option</span>
//   <div slot="content">
//     <ul>
//       <li>item 1</li>
//       <li>item 2</li>
//       <li>item 3</li>
//     </ul>
//   </div>
// </custom-dropdown>


const defineCustomElements = () => {
    class CustomDropdown extends HTMLElement {
      static get observedAttributes() {
        return ['position'];
      }
  
      constructor() {
        super();
        const shadow = this.attachShadow({ mode: "open" });
  
        // Create template
        const template = document.createElement("template");
        template.innerHTML = `
          <style>
            .container {
              position: relative;
              display: inline-block;
            }
  
            .label-container {
              display: flex;
              align-items: center;
              cursor: pointer;
              gap: 5px;
            }
  
            .arrow {
              transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            }
  
            .dropdown-content {
              position: absolute;
              z-index: 999;
              width: max-content;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              overflow: hidden;
              opacity: 0;
              transition: 
                opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              visibility: hidden;
            }
  
            /* Position variations */
            .dropdown-content[data-position="bottom"] {
              top: calc(100% + 1rem);
              left: 50%;
              transform: translateY(10px) translateX(-50%);
            }
  
            .dropdown-content[data-position="bottom-start"] {
              top: calc(100% + 1rem);
              left: 0;
              transform: translateY(10px);
            }
  
            .dropdown-content[data-position="bottom-end"] {
              top: calc(100% + 1rem);
              right: 0;
              transform: translateY(10px);
            }
  
            .dropdown-content[data-position="top"] {
              bottom: calc(100% + 1rem);
              left: 50%;
              transform: translateY(-10px) translateX(-50%);
            }
  
            .dropdown-content[data-position="top-start"] {
              bottom: calc(100% + 1rem);
              left: 0;
              transform: translateY(-10px);
            }
  
            .dropdown-content[data-position="top-end"] {
              bottom: calc(100% + 1rem);
              right: 0;
              transform: translateY(-10px);
            }
  
            .dropdown-content[data-position="left"] {
              top: 50%;
              right: calc(100% + 1rem);
              transform: translateY(-50%) translateX(-10px);
            }
  
            .dropdown-content[data-position="right"] {
              top: 50%;
              left: calc(100% + 1rem);
              transform: translateY(-50%) translateX(10px);
            }
  
            /* Active states for different positions */
            .container:hover .dropdown-content[data-position="bottom"],
            .container:hover .dropdown-content[data-position="bottom-start"],
            .container:hover .dropdown-content[data-position="bottom-end"] {
              opacity: 1;
              transform: translateY(0) translateX(-50%);
              visibility: visible;
            }
  
            .container:hover .dropdown-content[data-position="bottom-start"] {
              transform: translateY(0);
            }
  
            .container:hover .dropdown-content[data-position="bottom-end"] {
              transform: translateY(0);
            }
  
            .container:hover .dropdown-content[data-position="top"],
            .container:hover .dropdown-content[data-position="top-start"],
            .container:hover .dropdown-content[data-position="top-end"] {
              opacity: 1;
              transform: translateY(0) translateX(-50%);
              visibility: visible;
            }
  
            .container:hover .dropdown-content[data-position="top-start"] {
              transform: translateY(0);
            }
  
            .container:hover .dropdown-content[data-position="top-end"] {
              transform: translateY(0);
            }
  
            .container:hover .dropdown-content[data-position="left"] {
              opacity: 1;
              transform: translateY(-50%) translateX(0);
              visibility: visible;
            }
  
            .container:hover .dropdown-content[data-position="right"] {
              opacity: 1;
              transform: translateY(-50%) translateX(0);
              visibility: visible;
            }
  
            .container:hover .arrow {
              transform: rotate(180deg);
            }
  
            /* Buffer zones for different positions */
            .container::before {
              content: '';
              position: absolute;
              background: transparent;
            }
  
            /* Bottom positions buffer */
            .container:has(.dropdown-content[data-position^="bottom"])::before {
              top: 100%;
              left: 0;
              right: 0;
              height: 1rem;
            }
  
            /* Top positions buffer */
            .container:has(.dropdown-content[data-position^="top"])::before {
              bottom: 100%;
              left: 0;
              right: 0;
              height: 1rem;
            }
  
            /* Left position buffer */
            .container:has(.dropdown-content[data-position="left"])::before {
              top: 0;
              bottom: 0;
              right: 100%;
              width: 1rem;
            }
  
            /* Right position buffer */
            .container:has(.dropdown-content[data-position="right"])::before {
              top: 0;
              bottom: 0;
              left: 100%;
              width: 1rem;
            }
          </style>
  
          <div class="container">
            <div class="label-container">
              <slot name="label"></slot>
              <svg class="arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </div>
            <div class="dropdown-content">
              <slot name="content"></slot>
            </div>
          </div>
        `;
  
        shadow.appendChild(template.content.cloneNode(true));
        this.dropdownContent = shadow.querySelector('.dropdown-content');
      }
  
      connectedCallback() {
        // Set default position if none provided
        if (!this.hasAttribute('position')) {
          this.setAttribute('position', 'bottom');
        }
        this.updatePosition();
      }
  
      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'position' && oldValue !== newValue) {
          this.updatePosition();
        }
      }
  
      updatePosition() {
        const position = this.getAttribute('position') || 'bottom';
        this.dropdownContent.setAttribute('data-position', position);
      }
    }
  
    customElements.define("custom-dropdown", CustomDropdown);
  };
  
  defineCustomElements();
// ---------------------
